import React from 'react';
import Link from 'next/link';
import { DashboardOverviewResult } from '@/types/dashboard';

interface DashboardKpiCardsProps {
  overview: DashboardOverviewResult | null;
  hasFinancialAccess: boolean;
}

export default function DashboardKpiCards({
  overview,
  hasFinancialAccess,
}: DashboardKpiCardsProps) {
  if (!overview) return null;

  const { orders, returns, settlements, inventory, financial } = overview;
  const showFinancial = Boolean(hasFinancialAccess && financial);

  return (
    <div className="space-y-6">
      {/* Operational KPI Section */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-xs font-bold uppercase tracking-wider text-gray-500 flex items-center gap-2">
            <span className="flex h-5 w-5 items-center justify-center rounded-md bg-indigo-50 text-indigo-600 text-xs">⚡</span>
            <span>Operations &amp; Fulfillment Live Stream</span>
          </h2>
          <span className="text-[11px] text-gray-400 font-medium">Real-time marketplace sync</span>
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {/* Total Orders */}
          <div className="relative overflow-hidden rounded-xl bg-white p-5 shadow-xs border border-gray-200/80 hover:border-blue-300 hover:shadow-md transition-all group">
            <div className="flex items-center justify-between">
              <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                Total Orders
              </span>
              <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-blue-50 text-blue-600 text-sm group-hover:scale-110 transition-transform">
                📦
              </span>
            </div>
            <div className="mt-3 flex items-baseline justify-between">
              <p className="text-3xl font-extrabold text-gray-900 tracking-tight font-heading">
                {orders?.total ?? 0}
              </p>
              <span className="inline-flex items-center text-[11px] font-semibold text-blue-700 bg-blue-50 px-2 py-0.5 rounded-full">
                All Channels
              </span>
            </div>
            <div className="mt-3 flex items-center justify-between text-xs text-gray-400 border-t border-gray-100 pt-2.5">
              <span>Across connected channels</span>
              <Link href="/orders" className="text-blue-600 hover:text-blue-800 font-bold inline-flex items-center gap-0.5">
                View Orders ➔
              </Link>
            </div>
          </div>

          {/* Processing & Packed */}
          <div className="relative overflow-hidden rounded-xl bg-white p-5 shadow-xs border border-gray-200/80 hover:border-amber-300 hover:shadow-md transition-all group">
            <div className="flex items-center justify-between">
              <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                Pending Dispatch
              </span>
              <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-amber-50 text-amber-600 text-sm group-hover:scale-110 transition-transform">
                ⏳
              </span>
            </div>
            <div className="mt-3 flex items-baseline justify-between">
              <p className="text-3xl font-extrabold text-amber-600 tracking-tight font-heading">
                {(orders?.processing ?? 0) + (orders?.packed ?? 0)}
              </p>
              {(orders?.processing ?? 0) + (orders?.packed ?? 0) > 0 ? (
                <span className="inline-flex items-center text-[11px] font-semibold text-amber-800 bg-amber-100 px-2 py-0.5 rounded-full animate-pulse">
                  Action Needed
                </span>
              ) : (
                <span className="inline-flex items-center text-[11px] font-semibold text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full">
                  Queue Clear
                </span>
              )}
            </div>
            <div className="mt-3 flex items-center justify-between text-xs text-gray-400 border-t border-gray-100 pt-2.5">
              <span>{orders?.packed ?? 0} Packed · {orders?.processing ?? 0} In Progress</span>
              <Link href="/marketplaces/flipkart/packing" className="text-amber-600 hover:text-amber-800 font-bold inline-flex items-center gap-0.5">
                Packing Hub ➔
              </Link>
            </div>
          </div>

          {/* Delivered Orders */}
          <div className="relative overflow-hidden rounded-xl bg-white p-5 shadow-xs border border-gray-200/80 hover:border-emerald-300 hover:shadow-md transition-all group">
            <div className="flex items-center justify-between">
              <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                Delivered Orders
              </span>
              <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-emerald-50 text-emerald-600 text-sm group-hover:scale-110 transition-transform">
                ✅
              </span>
            </div>
            <div className="mt-3 flex items-baseline justify-between">
              <p className="text-3xl font-extrabold text-emerald-600 tracking-tight font-heading">
                {orders?.delivered ?? 0}
              </p>
              <span className="inline-flex items-center text-[11px] font-semibold text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-full">
                Fulfilled
              </span>
            </div>
            <div className="mt-3 flex items-center justify-between text-xs text-gray-400 border-t border-gray-100 pt-2.5">
              <span>Successfully reached customer</span>
              <span className="text-emerald-700 font-bold">100% SLA</span>
            </div>
          </div>

          {/* Returns & RTO */}
          <div className="relative overflow-hidden rounded-xl bg-white p-5 shadow-xs border border-gray-200/80 hover:border-rose-300 hover:shadow-md transition-all group">
            <div className="flex items-center justify-between">
              <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                Returns &amp; RTO
              </span>
              <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-rose-50 text-rose-600 text-sm group-hover:scale-110 transition-transform">
                🛡️
              </span>
            </div>
            <div className="mt-3 flex items-baseline justify-between">
              <p className="text-3xl font-extrabold text-rose-600 tracking-tight font-heading">
                {orders?.returned ?? 0}
              </p>
              {(orders?.returned ?? 0) > 0 ? (
                <span className="inline-flex items-center text-[11px] font-semibold text-rose-700 bg-rose-50 px-2 py-0.5 rounded-full">
                  SPF Eligible
                </span>
              ) : (
                <span className="inline-flex items-center text-[11px] font-semibold text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full">
                  0 Returns
                </span>
              )}
            </div>
            <div className="mt-3 flex items-center justify-between text-xs text-gray-400 border-t border-gray-100 pt-2.5">
              <span>Customer &amp; courier returns</span>
              <Link href="/marketplaces/flipkart/returns" className="text-rose-600 hover:text-rose-800 font-bold inline-flex items-center gap-0.5">
                Returns QC ➔
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Inventory & Quality Operational Indicators */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        {/* Channel Stock Available */}
        <div className="rounded-xl bg-white p-5 shadow-xs border border-gray-200/80 hover:border-indigo-300 hover:shadow-md transition-all">
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
              Channel Stock Available
            </span>
            <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-indigo-50 text-indigo-600 text-xs font-bold">
              🎯
            </span>
          </div>
          <p className="mt-2 text-2xl font-black text-indigo-600 tracking-tight font-heading">
            {inventory?.marketplaceAvailable ?? 0} <span className="text-xs font-normal text-gray-400">units live</span>
          </p>
          <div className="mt-3 flex items-center gap-3 text-xs text-gray-600 border-t border-gray-100 pt-2.5">
            <span className="inline-flex items-center gap-1.5 font-semibold bg-blue-50 text-blue-700 px-2 py-0.5 rounded-md border border-blue-100">
              <span className="h-2 w-2 rounded-full bg-blue-500" /> Flipkart: {inventory?.flipkart ?? 0}
            </span>
            <span className="inline-flex items-center gap-1.5 font-semibold bg-amber-50 text-amber-700 px-2 py-0.5 rounded-md border border-amber-100">
              <span className="h-2 w-2 rounded-full bg-amber-500" /> Amazon: {inventory?.amazon ?? 0}
            </span>
            <Link href="/inventory/pools" className="ml-auto text-xs font-bold text-indigo-600 hover:text-indigo-800">
              Pools ➔
            </Link>
          </div>
        </div>

        {/* Returns Under QC */}
        <div className="rounded-xl bg-white p-5 shadow-xs border border-gray-200/80 hover:border-orange-300 hover:shadow-md transition-all">
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
              Returns Under QC
            </span>
            <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-orange-50 text-orange-600 text-xs font-bold">
              🔍
            </span>
          </div>
          <p className="mt-2 text-2xl font-black text-orange-600 tracking-tight font-heading">
            {(returns?.received ?? 0) + (returns?.qcInProgress ?? 0)} <span className="text-xs font-normal text-gray-400">units awaiting QC</span>
          </p>
          <div className="mt-3 flex items-center justify-between text-xs text-gray-500 border-t border-gray-100 pt-2.5">
            <span>{returns?.total ?? 0} total returns registered</span>
            <Link href="/marketplaces/flipkart/returns?status=RECEIVED" className="text-xs font-bold text-orange-600 hover:text-orange-800">
              Inspect Dock ➔
            </Link>
          </div>
        </div>

        {/* Settlement Exceptions */}
        <div className="rounded-xl bg-white p-5 shadow-xs border border-gray-200/80 hover:border-purple-300 hover:shadow-md transition-all">
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
              Settlement Exceptions
            </span>
            <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-purple-50 text-purple-600 text-xs font-bold">
              ⚠️
            </span>
          </div>
          <p className="mt-2 text-2xl font-black text-purple-600 tracking-tight font-heading">
            {settlements?.exceptions ?? 0} <span className="text-xs font-normal text-gray-400">unreconciled</span>
          </p>
          <div className="mt-3 flex items-center justify-between text-xs text-gray-500 border-t border-gray-100 pt-2.5">
            <span>Unsettled orders: {settlements?.unsettledOrders ?? 0}</span>
            <Link href="/marketplaces/flipkart/settlements" className="text-xs font-bold text-purple-600 hover:text-purple-800">
              Reconcile Payouts ➔
            </Link>
          </div>
        </div>
      </div>

      {/* Authoritative Financial P&L Section (RBAC Protected) */}
      {showFinancial && (
        <div className="rounded-2xl border border-indigo-100 bg-gradient-to-br from-indigo-50/70 via-white to-blue-50/50 p-5 shadow-xs">
          <div className="flex items-center justify-between mb-4 border-b border-indigo-100/70 pb-3">
            <div className="flex items-center gap-2">
              <span className="flex h-6 w-6 items-center justify-center rounded-lg bg-indigo-600 text-white text-xs">💎</span>
              <div>
                <h2 className="text-xs font-bold uppercase tracking-wider text-indigo-950">
                  Authoritative Financial Health &amp; Net Realization
                </h2>
                <p className="text-[11px] text-gray-500">Marketplace payouts, commission deductions &amp; operating profits</p>
              </div>
            </div>
            <Link href="/profit-loss" className="rounded-lg bg-indigo-600/10 px-3 py-1 text-xs font-bold text-indigo-700 hover:bg-indigo-600/20 transition-colors">
              Full P&amp;L Analysis ➔
            </Link>
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <div className="rounded-xl bg-white p-4 border border-indigo-50/80 shadow-xs">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Gross Sales</p>
              <p className="mt-1 text-2xl font-black text-gray-900 font-mono tracking-tight">
                ₹{Number(financial?.grossSales ?? 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
              </p>
              <p className="mt-1 text-[11px] text-gray-400">
                Discounts: ₹{Number(financial?.discounts ?? 0).toFixed(2)}
              </p>
            </div>

            <div className="rounded-xl bg-white p-4 border border-indigo-50/80 shadow-xs">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Net Realized Payout</p>
              <p className="mt-1 text-2xl font-black text-blue-700 font-mono tracking-tight">
                ₹{Number(financial?.netSales ?? 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
              </p>
              <p className="mt-1 text-[11px] text-gray-400">
                Marketplace Fees: ₹{Number(financial?.totalMarketplaceFees ?? 0).toFixed(2)}
              </p>
            </div>

            <div className="rounded-xl bg-white p-4 border border-indigo-50/80 shadow-xs">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Gross Profit</p>
              <p className="mt-1 text-2xl font-black text-emerald-600 font-mono tracking-tight">
                ₹{Number(financial?.grossProfit ?? 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
              </p>
              <p className="mt-1 text-[11px] text-emerald-700 font-bold">
                Gross Margin: {Number(financial?.grossMarginPct ?? 0).toFixed(1)}%
              </p>
            </div>

            <div className="rounded-xl bg-white p-4 border border-indigo-50/80 shadow-xs">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Net Operating Profit</p>
              <p className="mt-1 text-2xl font-black text-indigo-700 font-mono tracking-tight">
                ₹{Number(financial?.netOperatingProfit ?? 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
              </p>
              <p className="mt-1 text-[11px] text-gray-400">
                OpEx Deductions: ₹{Number(financial?.operatingExpenses ?? 0).toFixed(2)}
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

